import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { User, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('PERSONAL');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success(t('success.welcomeBack'));
      
      if (user.userType === 'PERSONAL') {
        navigate('/personal/dashboard');
      } else {
        navigate('/enterprise/orgs');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Language Switcher - Top Right */}
      <div className="fixed top-4 right-4 ltr:right-4 rtl:left-4 z-50">
        <LanguageSwitcher variant="compact" />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl font-bold text-gray-900">{t('common.humatwin')}</h1>
            <p className="mt-2 text-gray-600">{t('auth.loginSubtitle')}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">

            <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
              <button
                data-testid="personal-login-tab"
                onClick={() => setUserType('PERSONAL')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                  userType === 'PERSONAL'
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4" />
                {t('auth.individual')}
              </button>
              <button
                data-testid="enterprise-login-tab"
                onClick={() => setUserType('ENTERPRISE')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                  userType === 'ENTERPRISE'
                    ? 'bg-white shadow-sm text-enterprise-slate'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Building2 className="w-4 h-4" />
                {t('auth.enterprise')}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.passwordPlaceholder')}
                      required
                      className="mt-1"
                    />
                  </div>

                  <Button
                    data-testid="login-submit-button"
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? t('common.loading') : t('auth.login')}
                  </Button>
                </form>

                <div className="text-center space-y-2">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-gray-500 hover:text-primary"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                  <br />
                  <Link
                    to="/signup"
                    className="text-sm text-primary hover:text-primary-hover"
                    data-testid="signup-link"
                  >
                    {t('auth.noAccount')} {t('auth.signupLink')}
                  </Link>
                </div>
          </div>
        </div>
      </div>

      <div 
        className={`hidden lg:flex flex-1 items-center justify-center p-12 ${
          userType === 'PERSONAL' 
            ? 'bg-gradient-to-br from-primary/10 to-personal-warm/10'
            : 'bg-gradient-to-br from-enterprise-slate/10 to-enterprise-steel/10'
        }`}
      >
        <div className="max-w-md text-center space-y-6">
          {userType === 'PERSONAL' ? (
            <>
              <h2 className="font-heading text-3xl font-bold text-gray-900">
                {t('auth.personalPanelTitle')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('auth.personalPanelDesc')}
              </p>
            </>
          ) : (
            <>
              <h2 className="font-heading text-3xl font-bold text-gray-900">
                {t('auth.enterprisePanelTitle')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('auth.enterprisePanelDesc')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
